import React, { ChangeEvent, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from 'yup'
import { checkChange } from "utils/checkChange";
import { imageToBase64 } from "utils/imageToBase64";
import { useAlert } from "hooks/useAlert";

import facebook from 'assets/social-media-icons/facebook.svg';
import twitter from 'assets/social-media-icons/twitter.svg';
import youtube from 'assets/social-media-icons/youtube.svg';
import instagram from 'assets/social-media-icons/instagram.svg';
import tiktok from 'assets/social-media-icons/tiktok.svg';
import { useToast } from "hooks/useToast";
import { CheckListProps, ContactProps, ContactTypeProps, DefaultStatusLead, LeadProps, SellersLeadPropertiesResponse, ShowCardProps, SocialMediaDictionary, StatusAnalysisLeadProps, StatusUsernameLeadProps, UserAnswerIsExactProps } from "../SellerLeadForm";
import { useHistory, useParams } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { formattingNumberToText } from "utils/formattingInputs";
import { noDdiPhone } from "utils/regex";

interface SellerLeadDetailsProps {
  id: string
}
interface ActionAlertProps {
  inputType?: string,
  inputName?: string,
}

const initInputsValues:LeadProps = {
  leadId: 0,
  clearsaleEntityId: '',
  firstname: '',
  email: '',
  cnpj: '',
  company: '',
  website: '',
  segmentId: 0,
  ecommercePlatformId: 0,
  billingRangeId: 0,
  amountRangeId: 0,  
  logo: '',
  phone: '',
  ramal: '',
  socialMediaLink: '',
  leadStatusId: 0,
  score: '',
  riskSuggestion: '',
  cnpjStatus: '',
  userId: 0,
  address: {
    zipcode: '',
    address: '',
    addressNumber: '',
    district: '',
    state: '',
    city: '',
  },
  contacts: [{
    contactTypeId: 0,
    title: '',
    contact: '',
  }],
  socialMedias: [
    {
      contactTypeId: 0,
      contact: '',
    }
  ],
}

const initTouchedInputsValues = {
  leadId: true,
  clearsaleEntityId: true,
  firstname: true,
  email: true,
  cnpj: true,
  company: true,
  website: true,
  segmentId: true,
  ecommercePlatformId: true,
  billingRangeId: true,
  amountRangeId: true, 
  logo: true,
  phone: true,
  ramal: true,
  socialMediaLink: true,
  leadStatusId: true,
  score: true,
  riskSuggestion: true,
  cnpjStatus: true,
  userId: true,
  contacts: [{
    contactTypeId: true,
    title: true,
    contact: true,
  }],
}

const initProperties = {
  billingRange: [{
    id: '',
    description: ''
  }],
  amountRange: [{
    id: '',
    description: '',
    sellerClassification: '',
  }],
  ecommercePlatform: [{
    id: '',
    description: ''
  }],
  segment: [{
    id: '',
    description: ''
  }],
  contactSocialMediaType: [{
    contactTypeId: 0, 
    contactType: ''
  }],
  contactType: [{
    contactTypeId: 0, 
    contactType: ''
  }],
}

const initUsers = [{
  userId: 0,
  userName: ''
}]

const initShowCard = {
  card: '',
}

const numberRangeToText = (rangeStart:number, rangeEnd?:number, currency?:boolean) => {

  const result = rangeEnd ? `De ${currency ? 'R\$' : ''}${formattingNumberToText(rangeStart)} a ${currency ? 'R\$' : ''}${formattingNumberToText(rangeEnd)}`
    : `Acima de ${currency ? 'R\$' : ''} ${formattingNumberToText(rangeStart)}`

  return result;
}

export const useSellerLeadDetails = () => {
  const {id} = useParams<SellerLeadDetailsProps>();
  const {setToast} = useToast();
  const { alert, setTextAlertInterface, openAlert, closeAlert } = useAlert();
  const {push} = useHistory();
  const {connectApi, connectAllApi} = useAuth();
  
  const [reasonToLeadReproved, setReasonToLeadReproved] = useState('')
  const [statusAnalysisLead, setStatusAnalysisLead] = useState<StatusAnalysisLeadProps[]>([]); 
  const [loadBusinessTrust, setLoadBusinessTrust] = useState(false);
  const [properties, setProperties] = useState<SellersLeadPropertiesResponse>(initProperties);
  const [contactTypes, setContactTypes] = useState<ContactTypeProps[]>([]);
  const [socialMediaTypes, setSocialMediaTypes] = useState<ContactTypeProps[]>();
  const [showCard, setShowCard] = useState<ShowCardProps>(initShowCard);
  const [usersOptions, setUsersOptions] = useState<StatusUsernameLeadProps[]>(initUsers); 
  const [actionAlert, setActionAlert] = useState<ActionAlertProps>();
  const [checklist, setChecklist] = useState<CheckListProps[]>([]); 
  const [checkListAnswered, setCheckListAnswered] = useState<UserAnswerIsExactProps[]>([]);
  const [initCheckListAnswered, setInitCheckListAnswered] = useState<UserAnswerIsExactProps[]>([]);
  const [imagePath, setImagePath] = useState<string>('');
  const [initialInputsValues, setInitialInputsValues] = useState<LeadProps>(initInputsValues);
  const [changeFooter, setChangeFooter] = useState<string[]>([]);
  const [submitButton, setSubmitButton] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [category, setCategory] = useState('');
  const [logoIsPNG, setLogoIsPNG] = useState(false);
  const [debounce, setDebounce] = useState<NodeJS.Timeout | any>();
  const {
    handleSubmit, 
    handleChange,
    resetForm, 
    setValues: setInputsValues, 
    values: inputsValues, 
    touched,
    errors,
  } = useFormik({
    initialValues: initInputsValues,
    initialTouched: initTouchedInputsValues,
    validationSchema: Yup.object({
      firstname: Yup.string().optional(),
      email: Yup.string().email().optional(),
      cnpj: Yup.string().required().matches(/^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}\-?\d{2}$/),
      company: Yup.string().required(),
      website: Yup.string().required(),
      logo: Yup.string().optional(),
      phone: Yup.string().matches(noDdiPhone).optional(),
      segmentId: Yup.number().nullable(true).optional(),
      ecommercePlatformId: Yup.number().nullable(true).optional(),
      billingRangeId: Yup.number().nullable(true).optional(),
      amountRangeId: Yup.number().nullable(true).optional(),
      userId: Yup.number().not([0]).required(),
      leadStatusId: Yup.number().not([0]),
      contacts: Yup.array().of(
        Yup.object({
          contactTypeId: Yup.number().optional(),
          contact: Yup.string().optional(),
          title: Yup.string().optional(),
        })
      ).min(1),
      socialMedias: Yup.array().of(
        Yup.object({
          contactTypeId: Yup.number().not([0]),
          contact: Yup.string(),
        })
      ),
    }),
    onSubmit: (_) => {
      sendData();
    }
  });

  //TRATAMENTO DE TEXTOS
  const originText = (props: string) => (
    {
    "BACKOFFICE": "Backoffice",
    "SITE_CONFI": "Confi",
    "CLEAR_EXTERNAL": "Clearsale"
  })[props]

  const changeNameTag = (props: string) => (
    {
      "userId": "Nome do responsável",
      "firstname": "Responsável",
      "company": "Nome da loja",
      "email": "Email",
      "phone": "Telefone",
      "cnpj": "CNPJ",
      "website": "URL da loja",
      "segmentId": "Categoria",
      "ecommercePlatformId": "Plataforma",
      "amountRangeId": "Quantidade de vendas / mês",
      "billingRangeId": "Faturamento mensal",
      "notificationApp": "Notificar no app",
      "contacts": "Contatos de suporte",
      "logo": "Logo",
      "socialMedias": "Redes Sociais",
      "statusLeadId": "Status do lead",
    }
  )[props];

  const socialMedias:SocialMediaDictionary = {
    'facebook': facebook,
    'twitter': twitter,
    'youtube': youtube,
    'instagram': instagram,
    'tiktok': tiktok,
  }

  //AÇÕES DO USUÁRIO
  const handleRemoveContact = (contactIndex: number) => {
    const newContacts = [...inputsValues.contacts];
    newContacts.splice(contactIndex, 1);

    setInputsValues({
      ...inputsValues,
      contacts: newContacts,
    })    
  }

  const handleAddContact = () => {
    const newContact:ContactProps = {
      leadContactId: 0,
      contactTypeId: 0,
      title: '',
      contact: '',
    }

    setInputsValues({
      ...inputsValues,
      contacts: [
        ...inputsValues.contacts,
        newContact,
      ]
    })
  }

  const handlerImageSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    if (event.target?.files) {
      const imageUrl = window.URL.createObjectURL(event.target.files[0]);
      const imageFile = event.target.files[0];
      if(imageFile.type === "image/png"){
        setLogoIsPNG(true)
        if(imageFile)
        imageToBase64(imageFile).then((result) => {
          setInputsValues({
            ...inputsValues,
            logo: result,
          })
        })
        setImagePath(imageUrl);
      }else{
        setLogoIsPNG(false)
        setImagePath('dont PNG');
      }
    }
  }

  const handleChangeRadioOfChecklist = (event: ChangeEvent<HTMLInputElement>) => {
    const name = Number(event.target.name);
    const value = event.target.value;

    checklist.forEach(item => {
      if(item.complianceStepid === name){
        const newChecklistAnswered: UserAnswerIsExactProps[] = [
          ...checkListAnswered.filter(item => item.complianceStepId !== name),
          {
            leadId: inputsValues.leadId || 0,
            complianceStepId: name,
            status: value === checklist.filter(item => item.complianceStepid === name)[0].expectedAnswer
          }
        ]
        setCheckListAnswered(newChecklistAnswered)
      }
    })

  }
  
  const debounceFN = (fn: any, value?: boolean) => {    
    clearTimeout(debounce);

    const timer = setTimeout(() => {
      value ? fn(value) : fn();
    }, 1500)

    setDebounce(timer);
  }

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    handleChange(event)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange(event)
  }

  const handleSaveAnswerToChecklist = async () => {
    connectApi({
      type: 'post',
      url: `/leads/${id}/steps`,
      body: checkListAnswered,
      successMessage: "Checklist salvo com sucesso",
      errorMessage: "Falha ao salvar checklist",
    });
  }

  const handleBusinessTrustEvaluation = async () => {
    setLoadBusinessTrust(true)
    await connectApi({
      type: 'post',
      url: `leads/${id}/do-verification`,
    })
    .then((_) => changeStatusLead(4))
    .finally(() => {
      setLoadBusinessTrust(false);
      closeAlert();
    });
  }

  const handleCallToSeller = (sellerId: number) => {
    push(`/sellers/${sellerId}`)
  }

  const changeStatusLead = async (status: number) => {
    await connectApi({
      type: 'put',
      url: `/leads/${id}/status`,
      body: {leadStatusId: status, userId: inputsValues.userId, reason:reasonToLeadReproved},
    }).then((response) => {
      const sellerId = response.data || "";
      if(status === 1 || status === 2){ 
        setToast({
          isActive:true,
          message: `Lead ${status === 1 ? 'aprovado' : 'reprovado'} com sucesso`,
          timeToRemoveToast: 3000,
          type: "success"
        })

        sellerId ? handleCallToSeller(sellerId) : getData();
      }else{
        setInputsValues({...inputsValues, leadStatusId: status})
        getData()
      }
    }).finally(() => {
      closeAlert();
    });
  }

  const setStatusToLead = (textDefault: DefaultStatusLead) => {
        
    const typeModal = textDefault === "approved" ? {
      title: "Aprovar varejista?",
      description: "Atenção! Essa ação não pode ser revertida.",
    } : textDefault === "reproved" ? {
      title: "Reprovar varejista?",
      description: "Atenção! Essa ação não pode ser revertida.",
    } : {
      title: "Seguir para Business Trust?",
      description: "Atenção! Essa ação não pode ser revertida.", 
    }

    setActionAlert({inputType: textDefault})
    setTextAlertInterface({
      positionCenter: true,
      closeAlertText: "Não",
      continueAlertText: "Sim",
      ...typeModal
    })
    openAlert(true, textDefault);
    
  }

  const discardChanges = () => {
    resetForm();
    setInputsValues({
      ...initialInputsValues
    });
    
    setImagePath("");
    closeAlert();
  }

  const saveFormChanges = () => {
    handleSubmit();
    if (Object.keys(errors).length > 0) {
      setToast({
        message: `Favor corrigir o(s) campo(s) indicado(s)`,
        isActive: true,
        type: 'error',
        timeToRemoveToast: 3000
      })
      closeAlert();
      return
    }
  }

  const showAlertForm = (typeOpen: boolean) => {
    setActionAlert({inputType: "formChanges"});
    setTextAlertInterface({
      positionCenter: false,
    })
    openAlert(typeOpen, 'sellerLead');
  }

  const validStatusLead = () => {
    if(inputsValues.userId){
      //CNPJ RECUSADO
      if(inputsValues.cnpjStatus !== 'ATIVA' && inputsValues.leadStatusId !== 2){
        setShowCard({card: "REPROVED_BY_CNPJ"})
        return
      }
      
      //CNPJ ACEITO
      if(inputsValues.leadStatusId === 5){
        setShowCard({card: "APPROVED_BY_CNPJ"})
        changeStatusLead(3)   
      }
      
      if(inputsValues.leadStatusId === 3){
        setShowCard({card: "APPROVED_BY_CNPJ"})
  
        if(checkListAnswered.length === checklist.length && checkListAnswered.filter(item => item.status === true).length === checklist.length){
          setShowCard({card: "APPROVED_BY_CHECKLIST"})     
          return
        }
        
        if(checkListAnswered.length && checkListAnswered.filter(item => item.status === false).length){
          setShowCard({card: "REPROVED_BY_CHECKLIST"})     
          return
        }
      }    
      
      if(inputsValues.leadStatusId === 4){
        setShowCard({card: "BUSINESS_TRUST"})     
        return
      }
  
      if(inputsValues.leadStatusId === 1){
        setShowCard({card: "APPROVED"})     
        return
      }
  
      if(inputsValues.leadStatusId === 2){
        setShowCard({card: "REPROVED"})     
        return
      }
    }
  }
  
  //FUNÇÕES DE PERSISTENCIA
  const getData = async () => {
    setIsLoading(true);
    connectAllApi([
      {type: 'get', url: '/sellers/properties'},
      {type: 'get', url: "/leads/steps"},
      {type: 'get', url: "/leads/status"},
      {type: 'get', url: "/users?pageNumber=1&rowsPerPage=10000"},
      {type: 'get', url: `/leads/${id}`},
      {type: 'get', url: `/leads/${id}/steps`},
    ]).then((responses) => {
      if(responses[1].status !== "rejected"){
        setChecklist(responses[1].value.data)
      }else{
        setToast({
          isActive: true,
          message: "Falha ao carregar checklist",
          timeToRemoveToast: 3000,
          type: "error"
        })
      }
      
      if(responses[2].status !== "rejected"){
        setStatusAnalysisLead(responses[2].value.data)
      }else{
        setToast({
          isActive: true,
          message: "Falha ao carregar lista de status",
          timeToRemoveToast: 3000,
          type: "error"
        })
      }
      
      if(responses[3].status !== "rejected"){
        setUsersOptions(responses[3].value.data)
      }else{
        setToast({
          isActive: true,
          message: "Falha ao carregar lista de usuários",
          timeToRemoveToast: 3000,
          type: "error"
        })
      }

      if(responses[4].status !== "rejected" && responses[0].status !== "rejected"){ 
        setHasError(false);
        const contactDecoded:ContactProps[] = responses[4].value.data.contacts;
        const socialMediasDecoded:ContactProps[] = responses[4].value.data.socialMedias;

        const socialMediaTypesDecoded:ContactTypeProps[] = responses[0].value.data?.contactSocialMediaType;
        const contactTypesDecoded:ContactTypeProps[] = responses[0].value.data?.contactType;

        const localProperties: SellersLeadPropertiesResponse = responses[0].value.data;
        const segment = localProperties.segment.map((item: any) => ({
          id: item.segmentId, 
          description: item.segment
        }));

        const ecommercePlatform = localProperties.ecommercePlatform.map((item: any) => ({
          id: item.ecommercePlatformId,
          description: item.ecommercePlatform
        }));

        const amountRange = localProperties.amountRange.map((item: any, index:any) => ({
          id: item.amountRangeId,
          description: index < localProperties.amountRange.length-1 
            ? numberRangeToText(item.amountRangeStart, item.amountRangeEnd) 
            : numberRangeToText(item.amountRangeStart),
          sellerClassification: item.sellerClassification,
        }));

        const billingRange = localProperties.billingRange.map((item: any, index:any) => ({
          id: item.billingRangeId, 
          description: index < localProperties.billingRange.length-1 ? 
            numberRangeToText(item.billingRangeStart, item.billingRangeEnd) 
            : numberRangeToText(item.billingRangeStart, 0, true)
        }))

        setProperties({
          ...localProperties,
          segment,
          ecommercePlatform,
          amountRange,
          billingRange
        });

        const inputsValues: LeadProps = {
          ...responses[4].value.data,
          phone: responses[4].value.data.phone.replaceAll('+55', ''),
          origin: originText(responses[4].value.data.origin),
          contacts: contactDecoded.length ? contactDecoded.map(item => {
            return {
              leadContactId: item.leadContactId,
              contactTypeId: item.contactTypeId,
              title: item.title,
              contact: item.contact?.replaceAll('+55', '')
            }
          }) : [{
            contactTypeId: 0,
            title: '',
            contact: '',
          }],
          socialMedias: socialMediaTypesDecoded.map(item => {
            const socialMedia = socialMediasDecoded.find((socialMedia) => socialMedia.contactTypeId === item.contactTypeId)
            return {
              sellerContactId: socialMedia?.leadContactId ?? 0,
              contactTypeId: item.contactTypeId,
              contact: socialMedia?.contact ?? '',
            }
          })
        }

        setInitialInputsValues({...inputsValues});
        setInputsValues({...inputsValues});
        setImagePath(inputsValues.logo);
        setContactTypes(contactTypesDecoded);
        setSocialMediaTypes(socialMediaTypesDecoded);

      }else{
        setHasError(true);
      }

      if(responses[5].status !== "rejected"){ 
        setInitCheckListAnswered([...responses[5].value.data]);
        setCheckListAnswered([...responses[5].value.data]);
      }else{
        setInitCheckListAnswered([]);
        setCheckListAnswered([]);
        setToast({
          isActive: true,
          message: "Falha ao carregar respostas do checklist",
          timeToRemoveToast: 3000,
          type: "error"
        })
      }
    })
    .catch((_) => setHasError(true))
    .finally(() => setIsLoading(false));
  }

  const sendData = async () => {
    const dataToSave = {
      ...inputsValues,
      userId: Number(inputsValues.userId),
      phone: inputsValues.phone
    }
    connectApi({
      type: 'put',
      url: `/leads/${id}`,
      body: dataToSave,
      successMessage: "Lead salvo com sucesso",
    }).then((_) => {
      setActionAlert({});
      closeAlert();
      getData();
    }).finally(() => closeAlert());
  }

  const compareArrayProperties = (firstArray: UserAnswerIsExactProps[], secondArray: UserAnswerIsExactProps[]) => {
    const maxLength = Math.max(firstArray.length, secondArray.length);
    const first = firstArray.map(item => item.status)
    const second = secondArray.map(item => item.status)

    if(firstArray.length !== secondArray.length) return true;

    for(let index = 0; index < maxLength; index++){
      if(first[index] !== second[index]){ 
        return true
      };
    }

    return false
  }

  useEffect(() => {
    checkListAnswered.length && compareArrayProperties(checkListAnswered, initCheckListAnswered) && debounceFN(handleSaveAnswerToChecklist)
  }, [checkListAnswered])

  useEffect(() => {
    if(actionAlert?.inputType === "reproved" && alert.typeContinue === "save"){
      if(inputsValues.leadStatusId === 3){
        changeStatusLead(2);
        setActionAlert({})
      }
      
      if(alert.typeContinue === "save" && inputsValues.leadStatusId !== 3){
        changeStatusLead(2);
        setActionAlert({})
      }
    }

    if(actionAlert?.inputType === "business-trust" && alert.typeContinue === "save"){
      handleBusinessTrustEvaluation();
      setActionAlert({})
    }

    if(actionAlert?.inputType === "approved" && alert.typeContinue === "save"){
      changeStatusLead(1);
      setActionAlert({})
    }
    
    if(actionAlert?.inputType === "formChanges"){
      alert.typeContinue === "save" && saveFormChanges();
      alert.typeContinue === "discard" && discardChanges();
    }
  }, [changeFooter, alert])
  
  useEffect(() => {
    const changed = checkChange(inputsValues, initialInputsValues, true);
    setChangeFooter(changed);
  }, [inputsValues])
  
  useEffect(() => {
    if(Number(inputsValues.amountRangeId) > 0){
      setCategory((properties.amountRange.filter(item => Number(item.id) === Number(inputsValues.amountRangeId))[0].sellerClassification || ''))
    }
  }, [inputsValues.amountRangeId])

  useEffect(() => {
    setInputsValues(initialInputsValues);
  }, [initialInputsValues])

  useEffect(() => {
    if(inputsValues.leadId){
      validStatusLead()
    }
  }, [inputsValues.cnpj, checkListAnswered])

  useEffect(() => {
    getData();
  }, [])

  return {
    handleChangeRadioOfChecklist,
    handleInputChange,
    checklist,
    inputsValues,
    touched,
    errors,
    handleSelectChange,
    properties,
    imagePath,
    handlerImageSelected,
    socialMedias,
    handleRemoveContact,
    handleAddContact,
    changeFooter,
    handleSubmit,
    showAlertForm,
    submitButton,
    changeNameTag,
    hasError,
    isLoading,
    statusAnalysisLead,
    usersOptions,
    category,
    checkListStatus: false,
    businnesTrust: false,
    originText,
    showCard,
    checkListAnswered,
    loadBusinessTrust,
    setStatusToLead,
    reasonToLeadReproved,
    setReasonToLeadReproved,
    handleCallToSeller,
    socialMediaTypes,
    contactTypes,
    initialInputsValues,
    logoIsPNG
  }
}